// src/services/partsInventoryService.js
import { getData, saveData } from './storageService';

export class PartsInventoryService {
  constructor() {
    this.parts = [];
    this.usageHistory = [];
    this.loadData();
  }

  async loadData() {
    try {
      this.parts = await getData('technician_parts') || this.generateInitialParts();
      this.usageHistory = await getData('parts_usage_history') || [];
    } catch (error) {
      console.error('Error loading parts data:', error);
      this.parts = this.generateInitialParts();
      this.usageHistory = [];
    }
  }

  async saveData() {
    await saveData('technician_parts', this.parts);
    await saveData('parts_usage_history', this.usageHistory);
  }

  generateInitialParts() {
    return [
      {
        id: 'pump_001',
        name: 'Water Pump L-12',
        category: 'pumps',
        currentStock: 3,
        minStock: 2,
        maxStock: 10,
        cost: 45.50,
        supplier: 'TechParts Inc.',
        leadTime: 7, // days
        lastUsed: '2024-01-10',
        critical: true
      },
      {
        id: 'valve_002',
        name: 'Pressure Valve 2.5"',
        category: 'valves',
        currentStock: 8,
        minStock: 5,
        maxStock: 20,
        cost: 12.75,
        supplier: 'FluidSystems',
        leadTime: 3,
        lastUsed: '2024-01-15',
        critical: false
      },
      {
        id: 'belt_003',
        name: 'Conveyor Belt 150cm',
        category: 'belts',
        currentStock: 2,
        minStock: 3,
        maxStock: 15,
        cost: 28.00,
        supplier: 'MotionTech',
        leadTime: 5,
        lastUsed: '2024-01-08',
        critical: true
      },
      {
        id: 'filter_004',
        name: 'Water Filter Carbon',
        category: 'filters',
        currentStock: 15,
        minStock: 10,
        maxStock: 30,
        cost: 8.50,
        supplier: 'PureWater Co.',
        leadTime: 2,
        lastUsed: '2024-01-12',
        critical: false
      },
      {
        id: 'sensor_005',
        name: 'Temperature Sensor',
        category: 'sensors',
        currentStock: 6,
        minStock: 4,
        maxStock: 12,
        cost: 32.25,
        supplier: 'SmartSensors Ltd.',
        leadTime: 4,
        lastUsed: '2024-01-14',
        critical: true
      }
    ];
  }

  // Використання запчастини
  async usePart(partId, quantity = 1, machineId, reason) {
    const part = this.parts.find(p => p.id === partId);
    if (!part) throw new Error('Part not found');

    if (part.currentStock < quantity) {
      throw new Error('Insufficient stock');
    }

    // Оновлюємо залишок
    part.currentStock -= quantity;
    part.lastUsed = new Date().toISOString().split('T')[0];

    // Додаємо в історію використання
    this.usageHistory.push({
      id: `usage_${Date.now()}`,
      partId,
      partName: part.name,
      quantity,
      machineId,
      reason,
      date: new Date().toISOString(),
      technician: 'current_user' // Буде замінено на реального користувача
    });

    await this.saveData();
    return part;
  }

  // Поповнення запасу
  async restockPart(partId, quantity, cost = null) {
    const part = this.parts.find(p => p.id === partId);
    if (!part) throw new Error('Part not found');

    part.currentStock += quantity;
    if (cost) part.cost = cost;

    await this.saveData();
    return part;
  }

  // Отримати запчастини, що потребують поповнення
  getPartsNeedingRestock() {
    return this.parts.filter(part => part.currentStock <= part.minStock);
  }

  // Отримати критичні запчастини
  getCriticalParts() {
    return this.parts.filter(part => part.critical && part.currentStock <= part.minStock * 1.5);
  }

  // Прогнозування потреб в запчастинах
  predictPartsNeeds(days = 30) {
    const predictions = [];
    const usageByPart = {};

    // Аналізуємо історичне використання
    this.usageHistory.forEach(usage => {
      if (!usageByPart[usage.partId]) {
        usageByPart[usage.partId] = [];
      }
      usageByPart[usage.partId].push(usage);
    });

    this.parts.forEach(part => {
      const partUsage = usageByPart[part.id] || [];
      const last30DaysUsage = partUsage.filter(usage => 
        new Date(usage.date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      );

      const avgMonthlyUsage = last30DaysUsage.reduce((sum, usage) => sum + usage.quantity, 0);
      const predictedUsage = (avgMonthlyUsage / 30) * days;
      
      const daysOfStock = part.currentStock / (avgMonthlyUsage / 30) || 999;

      predictions.push({
        partId: part.id,
        partName: part.name,
        currentStock: part.currentStock,
        predictedUsage: Math.round(predictedUsage * 100) / 100,
        daysOfStock: Math.round(daysOfStock),
        needsReorder: daysOfStock < part.leadTime * 1.5, // Замовляємо якщо залишку вистачить менше ніж на 1.5 терміну поставки
        reorderQuantity: Math.max(part.minStock * 2 - part.currentStock, 0)
      });
    });

    return predictions;
  }

  // Рекомендації по замовленню
  getReorderRecommendations() {
    const predictions = this.predictPartsNeeds();
    return predictions.filter(pred => pred.needsReorder).sort((a, b) => a.daysOfStock - b.daysOfStock);
  }

  // Аналіз вартості запасу
  getInventoryValue() {
    return this.parts.reduce((total, part) => total + (part.currentStock * part.cost), 0);
  }

  // Найбільш використовувані запчастини
  getMostUsedParts(limit = 10) {
    const usageCount = {};
    
    this.usageHistory.forEach(usage => {
      usageCount[usage.partId] = (usageCount[usage.partId] || 0) + usage.quantity;
    });

    return Object.entries(usageCount)
      .map(([partId, count]) => {
        const part = this.parts.find(p => p.id === partId);
        return {
          partId,
          partName: part?.name || 'Unknown',
          usageCount: count,
          category: part?.category
        };
      })
      .sort((a, b) => b.usageCount - a.usageCount)
      .slice(0, limit);
  }
}